<template>
  <div id="app">
    <navbar />
    <router-view
      :now="now"
      @push-notification="pushNotification"
      @show-modal="showModal"
    />
    <app-footer :meta="meta" :is-signed-in="isSignedIn" />
    <modal-container
      :modal="modal.component"
      :props="modal.props"
      @hide="hideModal"
    />
    <session-expiration-observer
      :now="now"
      @renew-token="renewToken"
      @sign-out="signOut"
      @token-expired="tokenExpired"
    />
    <notifications-list
      :notifications="notifications"
      @delete="deleteNotification"
    />
  </div>
</template>

<script>
import dayjs from "dayjs";

import { mapState, mapGetters } from "vuex";

import {
  PUSH_NOTIFICATION,
  DELETE_NOTIFICATION,
  CLOSE_COOKIES_NOTIFICATION,
  SHOW_MODAL,
  HIDE_MODAL,
  CLEAR_TOKEN,
  CLEAR_SENSITIVE_META_INFO
} from ":mutations";

import {
  REQUEST_META_INFO,
  REQUEST_FAVORED_MESSAGES,
  RENEW_TOKEN,
  REQUEST_TEMPLATES
} from ":actions";

import T from "%const/templates";

import Navbar from "%components/global/Navbar";
import AppFooter from "%components/global/AppFooter";
import NotificationsList from "%components/global/NotificationsList";
import ModalContainer from "%components/global/ModalContainer";
import SessionExpirationObserver from "%components/global/SessionExpirationObserver";

import { WithDispatcherProgress } from "%mixins";

import {
  CookiesNotification,
  SuccessNotification,
  TokenExpiredNotification,
  SignOutNotification
} from "%notifications";

export default {
  name: "app",

  components: {
    Navbar,
    AppFooter,
    NotificationsList,
    ModalContainer,
    SessionExpirationObserver
  },

  mixins: [WithDispatcherProgress],

  data() {
    return {
      now: dayjs(),
      timer: null
    };
  },

  computed: {
    ...mapState({
      notifications: state => state.global.notifications,
      cookiesNotificationShown: state => state.global.cookiesNotificationShown,
      modal: state => state.global.modal,
      meta: state => state.meta
    }),

    ...mapGetters(["isSignedIn", "jwtExpirationDate"])
  },

  beforeMount() {
    this.requestCommonData();
    this.showCookiesNotification();
    this.timer = setInterval(() => (this.now = dayjs()), 60000);
  },

  methods: {
    requestCommonData() {
      this.dispatchWithProgress("meta", REQUEST_META_INFO);
      this.dispatchWithProgress("favoredMessages", REQUEST_FAVORED_MESSAGES);
      this.dispatchWithProgress("footerTemplates", REQUEST_TEMPLATES, {
        templates: [
          T.Footer.TITLE,
          T.Footer.ADDRESS,
          T.Footer.DESC,
          T.Footer.PHONE,
          T.Footer.MAIL
        ]
      });
    },

    pushNotification({ component, props }) {
      this.$store.commit(PUSH_NOTIFICATION, { component, props });
    },

    deleteNotification(id) {
      this.$store.commit(DELETE_NOTIFICATION, { id });
    },

    showModal({ component, props }) {
      this.$store.commit(SHOW_MODAL, { component, props });
    },

    hideModal() {
      this.$store.commit(HIDE_MODAL);
    },

    showCookiesNotification() {
      if (this.cookiesNotificationShown) {
        this.$store.commit(PUSH_NOTIFICATION, {
          component: CookiesNotification,
          props: {
            beforeClose: () => this.$store.commit(CLOSE_COOKIES_NOTIFICATION)
          }
        });
      }
    },

    renewToken() {
      this.$store.dispatch(RENEW_TOKEN).then(() =>
        this.pushNotification({
          component: SuccessNotification,
          props: {
            content: `Twoja sesja została przedłużona do <strong>${this.jwtExpirationDate.format(
              "YYYY-MM-DD HH:mm:ss"
            )}</strong>.`,
            snackbar: 7
          }
        })
      );
    },

    async tokenExpired() {
      this.$store.commit(CLEAR_TOKEN);
      this.$store.commit(CLEAR_SENSITIVE_META_INFO);
      this.pushNotification({ component: TokenExpiredNotification });
      await this.$router.push("/");
      this.$immediatelyScrollToTop();
    },

    async signOut() {
      this.$store.commit(CLEAR_TOKEN);
      this.$store.commit(CLEAR_SENSITIVE_META_INFO);
      this.pushNotification({ component: SignOutNotification });
      await this.$router.push("/");
      this.$immediatelyScrollToTop();
    }
  },

  beforeDestroy() {
    clearInterval(this.timer);
  },

  watch: {
    now() {
      if (this.anyAction("error")) {
        this.requestCommonData();
      }
    }
  }
};
</script>

<style>
#app {
  overflow-x: hidden;
  max-width: 100%;
}
</style>
